import moment from 'moment';
import type { FormInstance } from 'element-plus';
import { getEndOfDayWithTime, getStartOfDayWithTime } from '../functions';
import type { SearchCounts, SearchResponseCount } from '@/types/types';

export const getDateRangeFromTimePeriod = (selectedTimePeriod) => {
	let newDateRange = [] as string[];

	switch (selectedTimePeriod) {
		case 'today':
			newDateRange = [moment().startOf('day').toString(), moment().endOf('day').toString()];
			break;
		case 'week':
			newDateRange = [moment().subtract(1, 'weeks').toString(), moment().endOf('day').toString()];
			break;
		case 'month':
			newDateRange = [moment().subtract(1, 'months').toString(), moment().endOf('day').toString()];
			break;
	}

	const fromDate = new Date(newDateRange[0]).toISOString();
	const toDate = new Date(newDateRange[1]).toISOString();

	return { fromDate, toDate };
};

export const handleClickTimePeriodOptionCheckbox = (item, selectedTimePeriod, timePeriodOptions) => {
	const newSelectedTimePeriod = [item.value];
	const newTimePeriodOptions = [...timePeriodOptions];
	let newDateRange = [] as string[];

	if (timePeriodOptions.find((option) => option.value === item.value).checked) {
		newTimePeriodOptions.forEach((option) => {
			option.checked = false;
		});
		newSelectedTimePeriod.length = 0;
	} else {
		newTimePeriodOptions.forEach((option) => {
			option.checked = option.value === item.value;
		});
	}

	switch (newSelectedTimePeriod[0]) {
		case 'today':
			newDateRange = [moment().startOf('day').toString(), moment().endOf('day').toString()];
			break;
		case 'week':
			newDateRange = [moment().startOf('isoWeek').toString(), moment().endOf('day').toString()];
			break;
		case 'month':
			newDateRange = [moment().startOf('month').toString(), moment().endOf('day').toString()];
			break;
	}

	return { newSelectedTimePeriod, newTimePeriodOptions, newDateRange };
};

interface TimePeriodOption {
	value: string;
	label: string;
	checked: boolean;
}

export const updateTimePeriodFromDateRange = (dateRange: string[]): string[] => {
	const start = moment(dateRange[0]);
	const end = moment(dateRange[1]);

	let selectedTimePeriod: string[] = [];
	if (start.isSame(moment().startOf('day'), 'day') && end.isSame(moment().endOf('day'), 'day')) {
		selectedTimePeriod = ['today'];
	} else if (start.isSame(moment().subtract(1, 'weeks'), 'day') && end.isSame(moment().endOf('day'), 'day')) {
		selectedTimePeriod = ['week'];
	} else if (start.isSame(moment().subtract(1, 'months'), 'day') && end.isSame(moment().endOf('day'), 'day')) {
		selectedTimePeriod = ['month'];
	}

	return selectedTimePeriod;
};

export const handleSearchDateRangeChange = (
	dateRange: string[],
	timePeriodOptions?: TimePeriodOption[],
): { fromDate: string | null; toDate: string | null; selectedTimePeriod: string[]; newTimePeriodOptions: TimePeriodOption[] } => {
	let fromDate: string | null = null;
	let toDate: string | null = null;
	let selectedTimePeriod: string[] = [];

	if (dateRange.length === 2) {
		fromDate = getStartOfDayWithTime(dateRange[0]);
		toDate = getEndOfDayWithTime(dateRange[1]);
		selectedTimePeriod = updateTimePeriodFromDateRange(dateRange);
	}

	let newTimePeriodOptions: TimePeriodOption[] = [];
	if (timePeriodOptions) {
		newTimePeriodOptions = timePeriodOptions.map((option) => ({
			...option,
			checked: selectedTimePeriod.includes(option.value),
		}));
	}

	return { fromDate, toDate, selectedTimePeriod, newTimePeriodOptions };
};

export const prepareSearchRequest = (request) => {
	const searchRequest: Record<string, any> = {};
	Object.entries(request).forEach(([key, val]) => {
		if (typeof val === 'object' && val && 'value' in val) {
			searchRequest[key] = val.value;
		} else {
			searchRequest[key] = val;
		}
	});
	return searchRequest;
};

export const checkIfDateInPast = (data: Date, allowToday = true) => {
	const date = moment(data);
	const now = moment().startOf('day');

	if (allowToday) {
		return date.isBefore(now, 'day');
	} else {
		return date.isBefore(now, 'day') || date.isSame(now, 'day');
	}
};

/**
 * Bringt `counts` einer Suchantwort in das Objektformat `{ key: anzahl }`.
 * TODO OEWR-91: Fallback nach Prod-Release entfernen — bis dahin kann das Backend noch das alte
 * Array-Format `[{ item1: key, item2: anzahl }]` liefern; danach reicht `counts` direkt.
 */
export const normalizeSearchCounts = (counts: unknown): SearchCounts | null => {
	if (counts === null || counts === undefined) return null;

	if (Array.isArray(counts)) {
		const normalized: SearchCounts = {};
		for (const entry of counts as SearchResponseCount[]) {
			if (entry && typeof entry.item1 === 'string') {
				normalized[entry.item1] = Number(entry.item2) || 0;
			}
		}
		return normalized;
	}

	if (typeof counts === 'object') return counts as SearchCounts;

	return null;
};
